import React, { useState, useEffect, useMemo } from 'react';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import { Link, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList, StructuredData } from 'components/SEO';
import { buildFaqSections, generateFAQSchema } from './faqData';
import { useTransactionFeeRate } from 'hooks/useTransactionFeeRate';
import './faq.css';

const FAQ_HERO_IMAGE =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/articles/faq_hero.png';

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showLoginModal, setShowLoginModal] = useState(false);
  // Fee figures in the answers read the live rate. While it loads, or if the
  // read fails, they show the published 1-5% range instead (never a blank or a
  // stale single number), and the FAQPage JSON-LD is built from the same text.
  const { ratePct, eliteDiscountPct, isLoading: isRateLoading } = useTransactionFeeRate();
  const faqSections = useMemo(
    () => buildFaqSections(ratePct, eliteDiscountPct),
    [ratePct, eliteDiscountPct],
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle sidebar navigation attempts
  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title="FAQ - TradeMagic | MTG Card Trading Platform"
        description="Get answers to common questions about TradeMagic, the peer-to-peer Magic: The Gathering card trading platform. Learn about point-based trading, security, and economics."
        canonical="https://trademagic.gg/faq"
        keywords="TradeMagic FAQ, MTG trading questions, point-based trading, Magic card trading platform, Cardsphere alternative, Deckbox alternative"
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'FAQ', url: 'https://trademagic.gg/faq' },
        ]}
      />

      {/* FAQPage Schema */}
      {/* Emitted once the rate read settles (loaded or failed), so the schema
          a crawler reads carries the live rate whenever one is available. */}
      {!isRateLoading && <StructuredData data={generateFAQSchema(faqSections)} />}

      <div className="faq-layout">
        {/* Mobile Menu Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="faq-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="faq-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/faq', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="faq-main">
            {/* Top Navigation Bar */}
            <TopNavigationBar
              title="FAQ"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="faq-container">
              {/* Hero Section */}
              <div
                className="faq-hero"
                style={{ backgroundImage: `url(${FAQ_HERO_IMAGE})` }}
              >
                <div className="faq-hero-overlay" />
                <img
                  src={FAQ_HERO_IMAGE}
                  alt="TradeMagic FAQ"
                  style={{ display: 'none' }}
                />
              </div>

              {/* Page Header */}
              <header className="faq-header">
                <h1 className="faq-title">Frequently Asked Questions</h1>
                <p className="faq-subtitle">
                  Everything you need to know about trading Magic: The Gathering
                  cards on TradeMagic
                </p>
              </header>

              {/* FAQ Sections */}
              {faqSections.map((section) => (
                <section key={section.title} className="faq-section">
                  <h2 className="faq-section-title">{section.title}</h2>
                  {section.items.map((item, index) => (
                    <div key={index} className="faq-item">
                      <div className="faq-question">{item.question}</div>
                      <div
                        className="faq-answer"
                        dangerouslySetInnerHTML={{ __html: item.answer }}
                      />
                    </div>
                  ))}
                  {section.aboutLink && (
                    <p className="faq-about-link">
                      Learn more on our <Link to="/about">About page</Link>.
                    </p>
                  )}
                </section>
              ))}

              {/* CTA Section */}
              <div className="faq-cta-section">
                <h2 className="faq-cta-title">Ready to Start Trading?</h2>
                <p className="faq-cta-description">
                  Join hundreds of Magic players trading cards efficiently on
                  TradeMagic. No haggling, no spreadsheets, just trading.
                </p>
                <button
                  className="faq-cta-button"
                  onClick={() => {
                    // A deliberate trading sign-in drops any stored return address (Asana 1220218671326301, Decision 3).
                    ImportSessionStorage.clearReturnTo();
                    navigate('/welcome');
                  }}
                >
                  Get Started Free
                </button>
              </div>
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default FAQ;
